import React, { createContext, useContext, useState, ReactNode } from 'react';

// Business settings sections (matching SettingsTab accordion sections)
export type BusinessSettingsSection =
  | 'business-info'
  | 'time-tracking'
  | 'employee-shifts'
  | 'integrations'
  | 'account';

interface BusinessSettingsContextType {
  isBusinessSettingsOpen: boolean;
  activeSection: BusinessSettingsSection;
  toggleBusinessSettings: () => void;
  openBusinessSettings: (section?: BusinessSettingsSection) => void;
  closeBusinessSettings: () => void;
  setActiveSection: (section: BusinessSettingsSection) => void;
}

const BusinessSettingsContext = createContext<BusinessSettingsContextType | undefined>(undefined);

export const useBusinessSettings = () => {
  const context = useContext(BusinessSettingsContext);
  if (context === undefined) {
    throw new Error('useBusinessSettings must be used within a BusinessSettingsProvider');
  }
  return context;
};

interface BusinessSettingsProviderProps {
  children: ReactNode;
}

export const BusinessSettingsProvider: React.FC<BusinessSettingsProviderProps> = ({ children }) => {
  const [isBusinessSettingsOpen, setIsBusinessSettingsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<BusinessSettingsSection>('business-info');

  const toggleBusinessSettings = () => {
    setIsBusinessSettingsOpen(prev => !prev);
  };

  const openBusinessSettings = (section?: BusinessSettingsSection) => {
    if (section) {
      setActiveSection(section);
    }
    setIsBusinessSettingsOpen(true);
  };

  const closeBusinessSettings = () => {
    setIsBusinessSettingsOpen(false);
  };

  return (
    <BusinessSettingsContext.Provider value={{
      isBusinessSettingsOpen,
      activeSection,
      toggleBusinessSettings,
      openBusinessSettings,
      closeBusinessSettings,
      setActiveSection
    }}>
      {children}
    </BusinessSettingsContext.Provider>
  );
};
